import { family, M } from '../palette';
import { blocks, bricks, MapBuilder, patches, planks, solid, speckle } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 320;
const G = 32;

function build(b: MapBuilder): MapMeta {
  b.ground(G, patches(family('grass'), 0.06, 5), speckle(family('dirt')), speckle([M('rock_1'), M('rock_2')]));
  // Concrete firing line.
  b.box(20, G - 1, 20, 300, G, 70, blocks(family('concrete'), 5));
  b.box(20, G, 68, 300, G + 10, 70, M('paint_yellow'));

  // Material test walls, weakest to strongest (left to right).
  const walls: [string, number | ((x: number, y: number, z: number) => number)][] = [
    ['glass', M('glass')],
    ['wood', planks(family('planks'))],
    ['brick', bricks(family('brick_red'))],
    ['stone', blocks(family('stone'), 3)],
    ['concrete', blocks(family('concrete'), 5)],
    ['metal', solid(family('metal'))],
    ['plate', M('plate')],
  ];
  walls.forEach(([, brush], i) => {
    const x0 = 28 + i * 40;
    b.box(x0, G, 150, x0 + 30, G + 40, 154, brush);
    if (i === 5) b.clear(x0 + 20, G, 150, x0 + 32, G + 40, 154); // gap for the watchtower stairs, which cross the metal wall
    b.box(x0, G - 1, 145, x0 + 30, G, 159, M('bedrock'));
  });

  // A two-story house to practice collapses on.
  b.building({ x0: 60, x1: 150, z0: 200, z1: 280, y0: G - 1, floors: 2, wall: bricks(family('brick_red')), doors: [{ side: 'n' }] });
  // A tall wooden watchtower on stilts.
  for (const [x, z] of [[200, 210], [236, 210], [200, 246], [236, 246]]) b.box(x, G, z, x + 4, G + 70, z + 4, M('log_1'));
  b.box(196, G + 70, 206, 244, G + 73, 254, planks(family('planks')));
  b.walls(196, G + 73, 206, 244, G + 83, 254, 2, planks(family('planks_dark')));
  b.climbEnding(250, G, 180, 's', 12, 70, planks(family('planks')));
  b.box(244, G + 69, 248, 262, G + 72, 256, planks(family('planks')));

  for (let i = 0; i < 6; i++) b.crate(40 + i * 44, G, 100);
  b.barrel(30, G, 90);
  b.barrel(290, G, 90, M('paint_blue'));
  b.tree(290, G, 290, 40, 14);
  b.tree(30, G, 300, 36, 12);

  b.walls(0, G, 0, S, G + 20, S, 3, bricks([M('brick_gray'), M('stone_2')]));

  const gy = G / 10;
  const meta = emptyMeta(gy, 16, 16);
  for (let i = 0; i < 8; i++) {
    meta.spawns.push({ team: -1, x: 4 + i * 3.4, y: gy, z: 4.5, yaw: Math.PI });
    meta.spawns.push({ team: 0, x: 4 + i * 3.4, y: gy, z: 4.5, yaw: Math.PI });
    meta.spawns.push({ team: 1, x: 4 + i * 3.4, y: gy, z: 30, yaw: 0 });
  }
  meta.points.push({ id: 'A', name: 'Range', x: 16, y: gy, z: 12, r: 3 });
  meta.pickups.push({ kind: 'health', x: 16, y: gy, z: 11 });
  meta.enemySpawns.push({ x: 16, y: gy, z: 30 }, { x: 4, y: gy, z: 30 }, { x: 28, y: gy, z: 30 });
  // The outer wall is a thin rim: nothing that moves may stand on it or beyond it (an invisible wall at every height from the rim's inner face).
  b.limitPlayRim(G + 15, 12);
  return meta;
}

export const trainingGrounds: MapDef = {
  id: 'training',
  name: 'Training Grounds',
  description: 'Test every weapon against every material.',
  size: [S, 128, S],
  modes: ['practice', 'tutorial', 'ffa', 'sandbox'],
  players: [1, 8],
  seed: 7,
  version: 4,
  theme: {
    skyTop: 0x5a9be6,
    skyHorizon: 0xd8ecf8,
    fog: 0xd0e4f2,
    fogNear: 40,
    fogFar: 120,
    sun: [0.5, 0.75, 0.3],
    sunColor: 0xfff4e0,
    sunIntensity: 1.0,
    ambient: 0xbcd4ff,
    ambientIntensity: 0.55,
    outside: 0x5f9e3c,
    music: 'training',
  },
  build,
};
